import type React from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface Options {
  /** How many rows the list holds. */
  count: number;
  /** Height to assume for a row nobody has measured yet. */
  estimate: number;
  /** Rows rendered above and below the viewport, so fast scrolling does not show gaps. */
  overscan?: number;
}

interface VirtualRows<T extends HTMLElement> {
  /** Goes on the element that scrolls. */
  scrollRef: React.RefObject<T>;
  /** The window to render: `[start, end)` of the caller's list. */
  start: number;
  end: number;
  /** Spacers standing in for the rows outside the window, in pixels. */
  paddingTop: number;
  paddingBottom: number;
  /** Goes on each rendered row, so its real height replaces the estimate. */
  measure: (index: number) => (element: HTMLElement | null) => void;
}

/*
 * Windowing for a list of rows whose heights are not known up front — a chat message may wrap to
 * one line or five, so nothing can be computed from a row count alone.
 *
 * Rows start at `estimate` and correct themselves as they are rendered and measured, which is what
 * keeps the scrollbar honest: the spacers above and below the window are built from real heights
 * wherever a row has been seen, and from the estimate everywhere else.
 *
 * The measured heights are keyed by index rather than by identity, so prepending to the list makes
 * every cached height point at the wrong row for one frame. They re-measure immediately and the
 * only symptom is a scrollbar that twitches, which is why this is not worth the bookkeeping to fix.
 */
export const useVirtualRows = <T extends HTMLElement>({
  count,
  estimate,
  overscan = 4
}: Options): VirtualRows<T> => {
  const scrollRef = useRef<T>(null);
  const [heights, setHeights] = useState<number[]>([]);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);

  /*
   * A layout effect, so the first real viewport height is known before the browser paints and the
   * list does not flash a single overscan-sized window.
   */
  useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return undefined;
    }

    const sync = (): void => {
      setScrollTop(element.scrollTop);
      setViewport(element.clientHeight);
    };

    sync();
    element.addEventListener('scroll', sync, { passive: true });

    /* The panel is inside a resizable column, so the viewport is not fixed for the session. */
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(sync);

    observer?.observe(element);

    return (): void => {
      element.removeEventListener('scroll', sync);
      observer?.disconnect();
    };
  }, []);

  const measure = useCallback(
    (index: number) =>
      (element: HTMLElement | null): void => {
        if (!element) {
          return;
        }

        const { height } = element.getBoundingClientRect();

        setHeights((previous) => {
          /* Returning the same array tells React there is nothing to re-render. */
          if (height <= 0 || previous[index] === height) {
            return previous;
          }

          const next = [...previous];

          next[index] = height;

          return next;
        });
      },
    []
  );

  return useMemo(() => {
    /* Running offsets, so the window can be found without walking the rows twice. */
    const offsets: number[] = [0];

    for (let index = 0; index < count; index += 1) {
      offsets.push(offsets[index] + (heights[index] ?? estimate));
    }

    let first = 0;

    while (first < count && offsets[first + 1] <= scrollTop) {
      first += 1;
    }

    let last = first;

    while (last < count && offsets[last] < scrollTop + viewport) {
      last += 1;
    }

    const start = Math.max(0, first - overscan);
    const end = Math.min(count, last + overscan);

    return {
      scrollRef,
      start,
      end,
      paddingTop: offsets[start],
      paddingBottom: offsets[count] - offsets[end],
      measure
    };
  }, [count, estimate, heights, measure, overscan, scrollTop, viewport]);
};
